import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {EditIcon, MapMarkerOutlineIcon, PriceIcon, RoomsIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Switcher} from "@pg-design/switcher-module";
import {Highlight, Text} from "@pg-design/text-module";

import {IRegionListRegion} from "../../../search/actions/fetch_search_regions_action";
import {HomeMatchBasicPreferencesFlowSource} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {
    getHomeMatchBasicBudgetText,
    getHomeMatchBasicRegionText,
    getHomeMatchBasicRoomsText,
    getHomeMatchBasicSummaryCopy
} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowPreferenceRow} from "./HomeMatchBasicPreferencesFlowPreferenceRow";

interface IProps {
    isOpen: boolean;
    source: HomeMatchBasicPreferencesFlowSource;
    values: IPreferencesDefaultValues;
    regionDetails?: IRegionListRegion[];
    homeMatchBasicConsent: boolean;
    isSaving: boolean;
    onHomeMatchBasicConsentChange: (value: boolean) => void;
    onEditPreferences: () => void;
    onConfirm: () => void;
    onClose: () => void;
}

export const HomeMatchBasicPreferencesFlowSummaryModal = (props: IProps) => {
    const {isOpen, source, values, regionDetails, homeMatchBasicConsent, isSaving, onHomeMatchBasicConsentChange, onEditPreferences, onConfirm, onClose} =
        props;
    const {title, description, buttonText} = getHomeMatchBasicSummaryCopy(source);

    return (
        <Modal isOpen={isOpen} className={modalStyle} onModalClose={onClose}>
            <Modal.CloseButton placement="right" variant="rounded" className={closeButtonStyle} />
            <Modal.Content className={summaryContentStyle}>
                <div className={headingWrapperStyle}>
                    <Text variant="headline_3" as="h2">
                        <Highlight>{title}</Highlight>
                    </Text>
                    <Text variant="body_copy_2" color="var(--colors-gray-800)">
                        {description}
                    </Text>
                </div>

                <section className={preferencesCardStyle}>
                    <div className={preferencesCardLeftColumnStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            Twoje preferencje
                        </Text>

                        <div className={preferencesListStyle}>
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={MapMarkerOutlineIcon} text={getHomeMatchBasicRegionText(values, regionDetails)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={RoomsIcon} text={getHomeMatchBasicRoomsText(values)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={PriceIcon} text={getHomeMatchBasicBudgetText(values)} />
                        </div>
                    </div>

                    <div className={preferencesCardRightColumnStyle}>
                        <Button variant="outlined_secondary" size="small" iconLeft={EditIcon} className={editButtonStyle} onClick={onEditPreferences}>
                            ZMIEŃ
                        </Button>
                    </div>
                </section>

                <section className={homeMatchCardStyle}>
                    <div className={homeMatchTextStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            <span id="home_match_basic_consent_label">Dopasujemy oferty dla Ciebie</span>
                        </Text>
                        <Text variant="body_copy_2" color="var(--colors-gray-700)">
                            Oszczędź czas na przeglądaniu setek ogłoszeń.{" "}
                            <span className={boldTextStyle}>Dzięki tej opcji automatycznie wysyłamy zapytania w Twoim imieniu</span> do deweloperów, którzy
                            budują to, czego szukasz. Pozwól, by to oni sami zgłaszali się do Ciebie z pasującymi ofertami. O najlepszych okazjach poinformujemy
                            Cię SMS-em i e-mailem.
                        </Text>
                    </div>
                    <Switcher
                        id="home_match_basic_consent"
                        checked={homeMatchBasicConsent}
                        checkedColor="var(--colors-success)"
                        onChange={onHomeMatchBasicConsentChange}
                        aria-labelledby="home_match_basic_consent_label"
                    />
                </section>

                <div className={mobileStickyActionBarStyle}>
                    <Button variant="filled_primary" size="medium" className={primaryButtonStyle} onClick={onConfirm} disabled={isSaving}>
                        {isSaving ? "ZAPISYWANIE..." : buttonText}
                    </Button>
                </div>
            </Modal.Content>
        </Modal>
    );
};

const modalStyle = css({
    sm: {
        width: "544px",
        borderRadius: 2
    }
});

const closeButtonStyle = css({
    m: 1.5,
    height: "3.6rem",
    width: "3.6rem"
});

const summaryContentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 1.5,
    pb: 11,

    sm: {
        gap: 3,
        p: 3
    }
});

const headingWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const preferencesCardStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 2,
    gap: 2
});

const preferencesCardLeftColumnStyle = css({
    flex: "1 1 auto"
});

const preferencesCardRightColumnStyle = css({
    flex: "0 0"
});

const editButtonStyle = css({
    flexShrink: 0
});

const preferencesListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    pt: 2
});

const homeMatchCardStyle = css({
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 1.5,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2
});

const homeMatchTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const boldTextStyle = css({
    fontWeight: 700
});

const primaryButtonStyle = css({
    width: "100%"
});

const mobileStickyActionBarStyle = css({
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    p: 1.5,
    backgroundColor: "var(--colors-white)",
    zIndex: 10,

    sm: {
        position: "static",
        p: 0,
        pt: 1
    }
});
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {EditIcon, MapMarkerOutlineIcon, PriceIcon, RoomsIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Switcher} from "@pg-design/switcher-module";
import {Highlight, Text} from "@pg-design/text-module";

import {IRegionListRegion} from "../../../search/actions/fetch_search_regions_action";
import {HomeMatchBasicPreferencesFlowSource} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {
    getHomeMatchBasicBudgetText,
    getHomeMatchBasicRegionText,
    getHomeMatchBasicRoomsText,
    getHomeMatchBasicSummaryCopy
} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowPreferenceRow} from "./HomeMatchBasicPreferencesFlowPreferenceRow";

interface IProps {
    isOpen: boolean;
    source: HomeMatchBasicPreferencesFlowSource;
    values: IPreferencesDefaultValues;
    regionDetails?: IRegionListRegion[];
    homeMatchBasicConsent: boolean;
    isSaving: boolean;
    onHomeMatchBasicConsentChange: (value: boolean) => void;
    onEditPreferences: () => void;
    onConfirm: () => void;
    onClose: () => void;
}

export const HomeMatchBasicPreferencesFlowSummaryModal = (props: IProps) => {
    const {isOpen, source, values, regionDetails, homeMatchBasicConsent, isSaving, onHomeMatchBasicConsentChange, onEditPreferences, onConfirm, onClose} =
        props;
    const {title, description, buttonText} = getHomeMatchBasicSummaryCopy(source);

    return (
        <Modal isOpen={isOpen} className={modalStyle} onModalClose={onClose}>
            <Modal.CloseButton placement="right" variant="rounded" className={closeButtonStyle} />
            <Modal.Content className={summaryContentStyle}>
                <div className={headingWrapperStyle}>
                    <Text variant="headline_3" as="h2">
                        <Highlight>{title}</Highlight>
                    </Text>
                    <Text variant="body_copy_2" color="var(--colors-gray-800)">
                        {description}
                    </Text>
                </div>

                <section className={preferencesCardStyle}>
                    <div className={preferencesCardLeftColumnStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            Twoje preferencje
                        </Text>

                        <div className={preferencesListStyle}>
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={MapMarkerOutlineIcon} text={getHomeMatchBasicRegionText(values, regionDetails)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={RoomsIcon} text={getHomeMatchBasicRoomsText(values)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={PriceIcon} text={getHomeMatchBasicBudgetText(values)} />
                        </div>
                    </div>

                    <div className={preferencesCardRightColumnStyle}>
                        <Button variant="outlined_secondary" size="small" iconLeft={EditIcon} className={editButtonStyle} onClick={onEditPreferences}>
                            ZMIEŃ
                        </Button>
                    </div>
                </section>

                <section className={homeMatchCardStyle}>
                    <div className={homeMatchTextStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            <span id="home_match_basic_consent_label">Dopasujemy oferty dla Ciebie</span>
                        </Text>
                        <Text variant="body_copy_2" color="var(--colors-gray-700)">
                            Oszczędź czas na przeglądaniu setek ogłoszeń.{" "}
                            <span className={boldTextStyle}>Dzięki tej opcji automatycznie wysyłamy zapytania w Twoim imieniu</span> do deweloperów, którzy
                            budują to, czego szukasz. Pozwól, by to oni sami zgłaszali się do Ciebie z pasującymi ofertami. O najlepszych okazjach poinformujemy
                            Cię SMS-em i e-mailem.
                        </Text>
                    </div>
                    <Switcher
                        id="home_match_basic_consent"
                        checked={homeMatchBasicConsent}
                        checkedColor="var(--colors-success)"
                        onChange={onHomeMatchBasicConsentChange}
                        aria-labelledby="home_match_basic_consent_label"
                    />
                </section>

                <div className={mobileStickyActionBarStyle}>
                    <Button variant="filled_primary" size="medium" className={primaryButtonStyle} onClick={onConfirm} disabled={isSaving}>
                        {isSaving ? "ZAPISYWANIE..." : buttonText}
                    </Button>
                </div>
            </Modal.Content>
        </Modal>
    );
};

const modalStyle = css({
    sm: {
        width: "544px",
        borderRadius: 2
    }
});

const closeButtonStyle = css({
    m: 1.5,
    height: "3.6rem",
    width: "3.6rem"
});

const summaryContentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 1.5,
    pb: 11,

    sm: {
        gap: 3,
        p: 3
    }
});

const headingWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const preferencesCardStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 2,
    gap: 2
});

const preferencesCardLeftColumnStyle = css({
    flex: "1 1 auto"
});

const preferencesCardRightColumnStyle = css({
    flex: "0 0"
});

const editButtonStyle = css({
    flexShrink: 0
});

const preferencesListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    pt: 2
});

const homeMatchCardStyle = css({
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 1.5,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2
});

const homeMatchTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const boldTextStyle = css({
    fontWeight: 700
});

const primaryButtonStyle = css({
    width: "100%"
});

const mobileStickyActionBarStyle = css({
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    p: 1.5,
    backgroundColor: "var(--colors-white)",
    zIndex: 10,

    sm: {
        position: "static",
        p: 0,
        pt: 1
    }
});
